import Button from "@/elements/Button";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputLabel,
  MenuItem,
  TextField,
} from "@mui/material";
import {
  UserEsternoCreateDto,
  UserEsternoDto,
  UserEsternoUpdateDto,
  UserRoleDto,
  getGetClientiQueryKey,
  getGetUsersQueryKey,
  useCreateCliente,
  useGetPortali,
  useUpdateCliente,
} from "@/api";
import {
  Controller,
  UseFormSetError,
  useForm,
  useWatch,
} from "react-hook-form";
import { withOpeningReset } from "../../utils/withOpeningReset";
import { SmallLoading } from "../Loading";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { addServerErrorsFactory } from "@/utils/addServerErrorsFactory";
import { languageOptions } from "@/utils/languages";
import { AxiosError } from "axios";
import { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import ClienteNodiSelector from "./ClienteNodiSelector";

function ClienteDialog({
  open = false,
  onClose,
  cliente,
}: {
  open?: boolean;
  onClose?: () => void;
  cliente?: UserEsternoDto | null;
}) {
  const { t } = useTranslation();
  const { data: portaliData } = useGetPortali();

  const handleClose = () => {
    onClose?.();
  };

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isValid },
    setValue,
  } = useForm<UserEsternoDto>({
    defaultValues: { lingua: "it", ...(cliente ?? {}) },
  });

  const { saveAsync, isLoading, error } = useMutator(cliente, setError, t);

  const queryClient = useQueryClient();

  const onSubmit = async (data: UserEsternoDto) => {
    try {
      await saveAsync(data);
      queryClient.invalidateQueries({ queryKey: getGetUsersQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetClientiQueryKey() });
      onClose?.();
    } catch (err) {}
  };

  const userRoles = useWatch({
    control: control,
    name: "userRoles",
  });
  const selectedPortali = useMemo(() => {
    if (!userRoles || !portaliData?.data) {
      return [];
    }

    return portaliData.data.filter(
      (p) => !!userRoles.find((x) => x.portaleId === p.id),
    );
  }, [userRoles, portaliData]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>
        {cliente
          ? t("clienteDialog.titoloModifica")
          : t("clienteDialog.titoloCrea")}
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="row" columnGap={1}>
          <Box flex={1}>
            <InputLabel htmlFor="user-nome">
              {t("clienteDialog.campi.nome")}
            </InputLabel>
            <TextField
              id="user-nome"
              autoFocus
              {...register("name", {
                required: t("validazione.campoObbligatorio"),
                minLength: 1,
              })}
              error={!!errors.name?.message}
              helperText={errors.name?.message || null}
              fullWidth
            />
          </Box>

          <Box flex={1}>
            <InputLabel htmlFor="user-cognome">
              {t("clienteDialog.campi.cognome")}
            </InputLabel>
            <TextField
              id="user-cognome"
              autoFocus
              {...register("lastName", {
                required: t("validazione.campoObbligatorio"),
                minLength: 1,
              })}
              error={!!errors.lastName?.message}
              helperText={errors.lastName?.message || null}
              fullWidth
            />
          </Box>
        </Box>

        <InputLabel htmlFor="user-email">
          {t("clienteDialog.campi.email")}
        </InputLabel>
        <TextField
          id="user-email"
          autoFocus
          {...register("email", {
            required: t("validazione.campoObbligatorio"),
            minLength: 1,
          })}
          error={!!errors.email?.message}
          helperText={errors.email?.message || null}
          fullWidth
        />

        <InputLabel htmlFor="user-lingua">
          {t("clienteDialog.lingua")}
        </InputLabel>
        <TextField
          id="user-lingua"
          select
          {...register("lingua", {
            required: t("validazione.campoObbligatorio"),
          })}
          error={!!errors.lingua?.message}
          helperText={errors.lingua?.message || null}
          fullWidth
        >
          {languageOptions.map((language) => (
            <MenuItem key={language.code} value={language.code}>
              {language.getLabel(t)}
            </MenuItem>
          ))}
        </TextField>

        <InputLabel>{t("clienteDialog.portali")}</InputLabel>
        <Controller
          name="userRoles"
          control={control}
          render={({ field }) => {
            return (
              <Box display="flex" flexDirection="column" gap={1} sx={{ ml: 2 }}>
                {(portaliData?.data ?? []).map((portale) => {
                  const checked = !!field.value?.find(
                    (x) => x.portaleId === portale.id && x.role === "Cliente",
                  );

                  return (
                    <FormControlLabel
                      key={portale.id}
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={(e) => {
                            const selected = e.target.checked;

                            if (selected) {
                              const newRoles = [...(field.value ?? [])];
                              newRoles.push({
                                role: "Cliente",
                                userId: cliente?.id ?? 0,
                                portaleId: portale.id,
                              });

                              setValue("userRoles", newRoles);
                            } else {
                              const newRoles: UserRoleDto[] = [];
                              for (const r of field.value ?? []) {
                                const skip =
                                  r.portaleId === portale.id &&
                                  r.role === "Cliente";
                                if (!skip) {
                                  newRoles.push(r);
                                }
                              }
                              setValue("userRoles", newRoles);
                            }
                          }}
                        />
                      }
                      label={portale.nome}
                    />
                  );
                })}
              </Box>
            );
          }}
        />

        <InputLabel>{t("clienteDialog.aziende.titolo")}</InputLabel>
        <Controller
          control={control}
          name="userEsternoPermessi"
          render={({ field }) => {
            return (
              <ClienteNodiSelector
                portali={selectedPortali}
                value={field.value ?? []}
                onChange={(permessi) => {
                  field.onChange(permessi);
                }}
              />
            );
          }}
        />
      </DialogContent>
      <DialogActions>
        {isLoading && <SmallLoading sx={{ mr: 1 }} />}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!isValid || isLoading}
          onClick={handleSubmit(onSubmit)}
        >
          {cliente
            ? t("clienteDialog.azioni.salva")
            : t("clienteDialog.azioni.crea")}
        </Button>
        <Button
          variant="contained"
          color="light"
          disabled={isLoading}
          onClick={handleClose}
        >
          {t("azioni.annulla")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withOpeningReset(ClienteDialog);

function useMutator(
  cliente: UserEsternoDto | null | undefined,
  setError: UseFormSetError<UserEsternoDto>,
  t: TFunction,
): {
  isLoading: boolean;

  error: AxiosError<unknown, any> | null;
  saveAsync: (data: UserEsternoDto) => Promise<any>;
} {
  const isNew = !cliente?.id;

  const create = useCreateCliente({
    mutation: {
      onSuccess: () => {
        toast.success(t("clienteDialog.toast.creato"));
      },
      onError: addServerErrorsFactory(setError, () => {
        toast.error(t("errori.richiesta"));
      }),
    },
  });

  const update = useUpdateCliente({
    mutation: {
      onSuccess: () => {
        toast.success(t("clienteDialog.toast.salvato"));
      },
      onError: addServerErrorsFactory(setError, () => {
        toast.error(t("errori.richiesta"));
      }),
    },
  });

  const { isLoading, error } = isNew ? create : update;

  return {
    isLoading,
    error,
    saveAsync: async (data: UserEsternoDto) => {
      return isNew
        ? create.mutateAsync({ data: data as UserEsternoCreateDto })
        : update.mutateAsync({
            id: cliente.id!,
            data: data as UserEsternoUpdateDto,
          });
    },
  };
}
